import { AdProviderTypes, createGooglePalContextId } from '@dicetechnology/dice-doris';
import type { PalSdkConfiguration } from '@dicetechnology/dice-unity/lib/services/realm/types';
import { AvailabilityState } from '@dicetechnology/dice-unity/lib/services/realm/types';
import { DisplayMode } from '@dicetechnology/dice-unity/lib/types/common';
import type { GooglePalNonceRequestInit } from '@dicetechnology/doris-plugin-google-pal';
import { GooglePalService, GooglePalTypes } from '@dicetechnology/doris-plugin-google-pal';

import pkg from '../../../package.json';

export class GooglePalManager {
    private _adProviders: AdProviderTypes[] = [];
    private _advertisingId: string | undefined;
    private _googlePalService?: GooglePalService;
    private _isEnabled: boolean = false;
    private _isOmSdkEnabled: boolean = false;
    private _isWhyThisAdIconEnabled: boolean = false;

    constructor(enableOmSdk: boolean, palSdkConfiguration?: PalSdkConfiguration) {
        this._isEnabled = palSdkConfiguration?.enableMode === AvailabilityState.ENABLED;
        this._isOmSdkEnabled = enableOmSdk;
        this._isWhyThisAdIconEnabled = palSdkConfiguration?.whyThisAdIcon === DisplayMode.SHOW;
        if (this._isEnabled) {
            this._adProviders = (palSdkConfiguration?.allows ?? []) as AdProviderTypes[];
        }
    }

    public async createGooglePalNonces(googlePalContextId: number): Promise<Map<string, string> | undefined> {
        try {
            if (!this._isEnabled) {
                return;
            }

            const context = this.getService()?.getContext(googlePalContextId);
            if (!context) {
                return;
            }

            const nonceRequestInit: GooglePalNonceRequestInit = {
                iconsSupported: this._isWhyThisAdIconEnabled,
                playerType: 'Chromecast',
                playerVersion: pkg.version,
                ppid: this._advertisingId ?? 'fallback-ads-id',
                videoHeight: window.devicePixelRatio * window.screen.height,
                videoWidth: window.devicePixelRatio * window.screen.width,
                continuousPlayback: false,
                descriptionUrl: document.location.origin,
                url: document.location.href
            };

            const googlePalNonces: Map<string, string> = new Map();
            const adProviders = this._adProviders;
            if (adProviders.includes(AdProviderTypes.MEDIATAILOR)) {
                if (this._isOmSdkEnabled) {
                    const { getOmidParameters } = await import('@dicetechnology/doris-plugin-datazoom');
                    const omidParameters = getOmidParameters();
                    nonceRequestInit.omidPartnerName = omidParameters.omidPartnerName;
                    nonceRequestInit.omidPartnerVersion = omidParameters.omidPartnerVersion;
                    nonceRequestInit.supportedApiFrameworks = omidParameters.supportedApiFrameworks;
                }

                // Reuse existing nonce manager in case auth token expired
                let nonceManager = context.getNonceManager(AdProviderTypes.MEDIATAILOR);
                if (!nonceManager) {
                    nonceManager = await context.createNonceManager(AdProviderTypes.MEDIATAILOR, nonceRequestInit);
                }

                googlePalNonces.set('CM-DVC-GIVN-MEDIATAILOR', nonceManager.getNonce());
            }

            if (adProviders.includes(AdProviderTypes.YOSPACE)) {
                if (this._isOmSdkEnabled) {
                    const { getOmidParameters } = await import('@dicetechnology/doris-plugin-yospace-ssai');
                    const omidParameters = getOmidParameters();
                    nonceRequestInit.omidPartnerName = omidParameters.omidPartnerName;
                    nonceRequestInit.omidPartnerVersion = omidParameters.omidPartnerVersion;
                    nonceRequestInit.supportedApiFrameworks = omidParameters.supportedApiFrameworks;
                }

                // Reuse existing nonce manager in case auth token expired
                const nonceManager = await context.createNonceManager(AdProviderTypes.YOSPACE, nonceRequestInit);
                googlePalNonces.set('CM-DVC-GIVN-YOSPACE', nonceManager.getNonce());
            }

            return googlePalNonces;
        } catch (error) {
            return;
        }
    }

    public async generateGooglePalContextId() {
        if (this._isEnabled) {
            return await createGooglePalContextId({
                adProviders: this._adProviders,
                googlePalService: this._googlePalService
            });
        }
    }

    public getAdProviders() {
        return this._adProviders;
    }

    public getIsEnabled() {
        return this._isEnabled;
    }

    public getService() {
        if (!this._isEnabled) {
            return;
        }

        if (!this._googlePalService) {
            this._googlePalService = new GooglePalService({ type: GooglePalTypes.GOOGLE_PAL_CAST });
        }

        return this._googlePalService;
    }

    public setAdvertisingId(advertisingId?: string) {
        this._advertisingId = advertisingId;
    }
}
